import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { filter, Observable, ReplaySubject, switchMap, takeUntil, tap } from 'rxjs';
import { debounceTime, finalize } from 'rxjs/operators';
import { Faq, SearchResult, SearchResultTypes } from 'shared/types/search-result.type';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { HighlightPipe } from 'shared/pipes/highlight.pipe';
import { TruncateBeforePipe } from 'shared/pipes/truncate-before.pipe';
import { IPagination } from 'shared/types/pagination.interface';
import { KorviuScrollbarComponent } from 'shared/components/korviu-scrollbar/korviu-scrollbar.component';
import { KorviuInputComponent } from 'shared/components/korviu-input/korviu-input.component';

type searchFnType = (query: string, pagination: IPagination) => Observable<SearchResult[]>;

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    NzButtonModule,
    TranslateModule,
    NgClass,
    ReactiveFormsModule,
    NzSpinComponent,
    HighlightPipe,
    TruncateBeforePipe,
    KorviuScrollbarComponent,
    KorviuInputComponent,
    NgTemplateOutlet
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss'
})
export class SearchComponent implements OnInit, OnDestroy {
  @Input() public searchFn: searchFnType;
  @Input() public inputType: 'header' | 'faq' = 'header';
  @Output() searchResultClick: EventEmitter<SearchResult>;
  @Output() emptyQuery: EventEmitter<void>;

  @ViewChild('mobileSearchInput') public mobileSearchInputComponent: ElementRef;

  public searchForm: FormGroup;
  public searchInFocus: boolean;

  public searchResults: SearchResult[];
  public searchResultsIsLoading: boolean;
  public searchResultsWasLoaded: boolean;
  public loadMoreIsLoading: boolean;
  public SearchResultTypes: typeof SearchResultTypes = SearchResultTypes;
  public lastQuery: string;

  public get searchQueryControl(): AbstractControl {
    return this.searchForm?.get('query');
  }

  private _pagination: IPagination;
  private readonly _onDestroy$: ReplaySubject<void>;

  constructor() {
    this.searchResultClick = new EventEmitter<SearchResult>();
    this.emptyQuery = new EventEmitter<void>();
    this.loadMoreIsLoading = false;
    this._pagination = { page: 1, pageSize: 10 };
    this.lastQuery = '';
    this.searchForm = new FormGroup({
      query: new FormControl('')
    });
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public ngOnInit(): void {
    this.searchQueryControl.valueChanges
      .pipe(
        tap((value: string) => {
          this._resetPagination();
          if (!value) {
            this._resetSearch();
          }
        }),
        debounceTime(1000),
        tap((value: string) => {
          if (!value) {
            this.emptyQuery.emit();
          }
        }),
        filter((value: string) => value?.length > 2),
        tap(() => {
          this.searchResultsWasLoaded = false;
          this.searchResultsIsLoading = true;
        }),
        switchMap((query: string) => {
          this.lastQuery = query;
          return this.searchFn(query, this._pagination);
        }),
        tap(() => {
          this.searchResultsIsLoading = false;
          this.searchResultsWasLoaded = true;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((results: SearchResult[]) => {
        this.searchResults = results;
      });
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public clearSearch(): void {
    this.searchQueryControl.patchValue('');
  }

  public onScrollReachedBottom(): void {
    this._loadMore();
  }

  public onMobileSearchButtonClick(): void {
    this.searchInFocus = true;
    setTimeout(() => {
      this.mobileSearchInputComponent.nativeElement.focus();
    });
  }

  public onSearchInputBlur(): void {
    setTimeout(() => {
      this.searchInFocus = false;
    }, 300);
  }

  public getSearchResultAsAny(result: SearchResult): any {
    return result as any;
  }

  public getSearchResultText(result: SearchResult): string {
    let text: string;
    if (result?.type === SearchResultTypes.FAQ || this.inputType === 'faq') {
      text = (result as Faq)?.question;
    } else {
      text = (result as any)?.description;
    }

    return text;
  }

  public onSearchResultClick(result: SearchResult): void {
    this.searchResultClick.emit(result);
    setTimeout(() => {
      this.searchInFocus = false;
      this.searchForm.reset();
    }, 300);
  }

  private _loadMore(): void {
    this._pagination.page++;
    this.loadMoreIsLoading = true;
    this.searchFn(this.searchQueryControl.value, this._pagination)
      .pipe(
        finalize(() => {
          this.loadMoreIsLoading = false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((results: SearchResult[]) => {
        this.searchResults = this.searchResults.concat(results);
      });
  }

  private _resetSearch(): void {
    this.searchResultsWasLoaded = false;
    this.searchResults = [];
    this._resetPagination();
  }

  private _resetPagination(): void {
    this._pagination = { page: 1, pageSize: 10 };
  }
}
